<template>
  <div class="app terminal">
    <div class="titlebar">
      <div class="traffic-lights">
        <div class="red"></div>
        <div class="yellow"></div>
        <div class="green"></div>
      </div>
      <div class="title">Terminal</div>
      <div></div>
    </div>
    <div class="console" ref="console">
      <div class="line" v-for="(line, i) in lines" :key="i">{{ line }}</div>
    </div>
  </div>
</template>

<script>
import {sleep} from '@/utils'
import shortHash from 'short-hash'

const HUMAN_CLI_PROMPT = "human-cli v5.0.91"

const HUMAN_OUTPUT = "/human/out"

const PROGRESS_CHARS = ["|", "/", "-", "\\"]

const BUILD_STEPS = [
  "FROM mum-and-dad:latest",
  "RUN mkdir /human",
  "WORKDIR /human",
  "ADD genetics.json /human",
  "ADD name-ideas.json /human",
  "ADD core-values.json /human",
  [
      `RUN human-cli build --input *.json --output ${HUMAN_OUTPUT}`,
      [
        "++             ",
        HUMAN_CLI_PROMPT,
        "Building new human...",
        "<progress>",
        `Complete. New human has been output to ${HUMAN_OUTPUT}`
      ]
  ],
  [
    `RUN human-cli make-unique --human ${HUMAN_OUTPUT}`,
      [
          "++               ",
          HUMAN_CLI_PROMPT,
          `Making ${HUMAN_OUTPUT} unique...`,
          "<progress>",
          "Complete. Congratulations! It's a boy."
      ]
  ],
  `ENTRYPOINT  ["human-cli", "spawn", "${HUMAN_OUTPUT}"]`
]

export default {
  data () {
    return {
      lines: []
    }
  },
  methods: {
    ready () {
      this.run()
    },
    async run () {
      await sleep(300)
      await this.writePrompt()
      await sleep(800)
      await this.type("docker build -t new-human .")
      await sleep(500)
      await this.writeLine("Sending build context to Docker daemon  6.931MB")
      for (const [i, step] of BUILD_STEPS.entries()) {
        if(Array.isArray(step)) {
          await this.writeLine(`Step ${i + 1}/${BUILD_STEPS.length} : ${step[0]}`)
          for(const commandOutput of step[1]) {
            if (commandOutput === '<progress>') {
              await this.showProgress(5500)
            } else if (commandOutput.startsWith("++")) {
              await this.writeLine(" ")
              await this.type(commandOutput.replace("++", ""))
            } else {
              await this.writeLine(commandOutput)
            }
          }
          await this.writeLine(` ---> ${shortHash(step[0])}`, 700)
        } else {
          await this.writeLine(`Step ${i + 1}/${BUILD_STEPS.length} : ${step}`)
          await this.writeLine(` ---> ${shortHash(step)}`, 700)
        }
      }
      await this.writeLine("Successfully built new-human")
      await this.writePrompt()
      await sleep(1350)
      await this.type("docker run new-human")
      await sleep(1200)
      await this.writeLine(HUMAN_CLI_PROMPT, 850)
      await this.writeLine(`spawning ${HUMAN_OUTPUT}`, 1000)
      this.$emit('complete')
    },
    async writePrompt () {
      await this.writeLine("/root # ")
    },
    async writeLine (line, delay = 300) {
      this.lines.push(line)
      this.scroll()
      await sleep(delay)
    },
    async replaceLine (line, delay = 300) {
      this.lines.pop()
      this.lines.push(line)
      this.scroll()
      await sleep(delay)
    },
    async removeLine (delay = 300) {
      this.lines.pop()
      this.scroll()
      await sleep(delay)
    },
    async showProgress (duration = 2500, interval = 500) {
      let i = 0
      const finish = new Date()
      finish.setMilliseconds(finish.getMilliseconds() + duration)
      await this.writeLine(PROGRESS_CHARS[0])
      return new Promise((resolve) => {
        const int = setInterval(async () => {
          if(new Date() > finish) {
            clearInterval(int)
            await this.removeLine(0)
            return resolve()
          }
          i += 1
          let newChar = PROGRESS_CHARS[i]
          if (!newChar) {
            i = 0
            newChar = PROGRESS_CHARS[0]
          }
          await this.replaceLine(newChar, 0)
        }, interval)
      })
    },
    async type (chars) {
      for(const char of chars.split("")) {
        await sleep(120)
        let currentLine = this.lines[this.lines.length - 1]
        currentLine += char
        this.lines.pop()
        this.lines.push(currentLine)
        this.scroll()
      }
    },
    scroll () {
      this.$nextTick(() => {
        this.$refs.console.scrollTop = this.$refs.console.scrollHeight
      })
    }
  },
  mounted () {
    this.ready()
  },
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/mixins";

.terminal {
  background-color: #002B36;
  width: 800px;
  height: 450px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 80%;
  max-width: 90%;
  @include box-shadow(0, 0, 20px, rgba(0, 0, 0, 0.15));
  @include border-radius(4px);
  @include animation(load 0.3s);

  .titlebar {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 2px 10px;
    @include css3-prefix(box-shadow, 'inset 0px 0.5px 0.5px rgba(255, 255, 255, 0.5), inset 0px -0.5px 0px #B7B4B7, inset 0px -1px 0px rgba(183, 180, 183, 0.3)');
    @include background-gradient(rgb(237, 236, 237), rgb(210, 209, 210));

    > * {
      flex-basis: 33%;
    }

    .traffic-lights {
      display: flex;
      flex-direction: row;
      align-items: center;

      > * {
        width: 12px;
        height: 12px;
        border-width: 1px;
        border-style: solid;
        margin-right: 6px;
        @include border-radius(12px)
      }

      .red {
        background-color: #FF6157;
        border-color: #E24640;
      }

      .yellow {
        background-color: #FFC12F;
        border-color: #DFA023;
      }

      .green {
        background-color: #2ACB42;
        border-color: #1BAC2C;
      }
    }

    .title {
      font-size: 14px;
      text-align: center;
    }
  }

  .console {
    flex-grow: 1;
    padding: 5px;
    overflow-y: auto;
    color: #839496;
    font-family: Monaco, monospace;
  }
}

@include keyframes(load) {
  0% {
    @include transform(scale(0.97));
    opacity: 0;
  }

  100% {
    @include transform(scale(1));
    opacity: 1;
  }
}
</style>
